import {
  getProjectById,
  listInitiativesByProject,
  listProjectMembers,
  listProjectsForClient,
} from '@infosaas/shared'
import { useQueries, useQuery } from '@tanstack/react-query'
import { supabase } from '../../../shared/lib/supabase'
import { useAuthStore } from '../../../shared/stores/authStore'

/**
 * Hooks granulares do projeto.
 *
 * Antes: 1 query monolítica buscava project + initiatives + members em paralelo
 * com chave única. Invalidar membros invalidava o projeto inteiro.
 *
 * Agora: 3 queries com chaves independentes, expostas individualmente e
 * compostas em `useClientProject` pra manter a API que o resto do app usa.
 */

const enabledId = (id) => Number.isFinite(Number(id))

export function useClientProjects() {
  const clientUserId = useAuthStore((s) => s.profile?.id)
  return useQuery({
    queryKey: ['client-projects', clientUserId],
    queryFn: () => listProjectsForClient(supabase, clientUserId),
    enabled: !!clientUserId,
  })
}

export function useProjectCore(projectId) {
  const id = Number(projectId)
  return useQuery({
    queryKey: ['project-core', id],
    queryFn: () => getProjectById(supabase, id),
    enabled: enabledId(id),
  })
}

export function useProjectInitiatives(projectId) {
  const id = Number(projectId)
  return useQuery({
    queryKey: ['project-initiatives', id],
    queryFn: () => listInitiativesByProject(supabase, id),
    enabled: enabledId(id),
  })
}

export function useProjectMembers(projectId) {
  const id = Number(projectId)
  return useQuery({
    queryKey: ['project-members', id],
    queryFn: () => listProjectMembers(supabase, id),
    enabled: enabledId(id),
  })
}

/**
 * Composição backward-compatible: retorna `{ ...project, initiatives, members }`
 * + um `isLoading`/`error` agregado. Mantém a forma que ProjectProvider,
 * InitiativeProvider e páginas de dashboard já esperam.
 */
export function useClientProject(projectId) {
  const id = Number(projectId)
  const enabled = enabledId(id)

  const queries = useQueries({
    queries: [
      {
        queryKey: ['project-core', id],
        queryFn: () => getProjectById(supabase, id),
        enabled,
      },
      {
        queryKey: ['project-initiatives', id],
        queryFn: () => listInitiativesByProject(supabase, id),
        enabled,
      },
      {
        queryKey: ['project-members', id],
        queryFn: () => listProjectMembers(supabase, id),
        enabled,
      },
    ],
  })

  const [core, initiatives, members] = queries
  const isLoading = queries.some((q) => q.isLoading)
  const error = queries.find((q) => q.error)?.error ?? null
  const data =
    core.data && initiatives.data && members.data
      ? { ...core.data, initiatives: initiatives.data, members: members.data }
      : null

  return {
    data,
    isLoading,
    error,
    isFetching: queries.some((q) => q.isFetching),
    refetch: () => Promise.all(queries.map((q) => q.refetch())),
  }
}
